// packages/components/src/legal/renderContentItem.tsx
import { AlertTriangle, Check, CheckCircle, CheckSquare, Quote } from 'lucide-react';

type ListItem = string | { html?: string; text?: string };

type ContentItem = {
  type: 'paragraph' | 'subheading' | 'list' | 'alertBox' | 'checkbox' | 'recitalBox';
  html?: string;
  text?: string;
  title?: string;
  label?: string;
  ordered?: boolean;
  items?: ListItem[];
};

const createMarkup = (htmlString: string) => ({ __html: htmlString });

const resolveContent = (item: ContentItem) => item.text ?? item.html ?? '';

const resolveListItem = (item: ListItem) =>
  typeof item === 'string' ? item : item?.html ?? item?.text ?? '';

export const renderContentItem = (item: ContentItem, index: number) => {
  switch (item.type) {
    case 'paragraph':
      return (
        <p
          key={index}
          className="mb-4 text-slate-700 leading-relaxed"
          dangerouslySetInnerHTML={createMarkup(resolveContent(item))}
        />
      );

    case 'subheading':
      return (
        <h3
          key={index}
          className="text-xl font-semibold text-gray-800 mt-8 mb-3"
          dangerouslySetInnerHTML={createMarkup(resolveContent(item))}
        />
      );

    case 'list': {
      const items = Array.isArray(item.items) ? item.items : [];
      const isOrdered = Boolean(item.ordered);
      const ListComponent = isOrdered ? 'ol' : 'ul';
      const listClassName = isOrdered ? 'list-decimal pl-5' : 'list-none pl-0';

      return (
        <ListComponent key={index} className={`space-y-3 mb-4 ${listClassName}`}>
          {items.map((listItem, liIndex) => {
            const html = resolveListItem(listItem);
            return (
              <li
                key={liIndex}
                className={isOrdered ? 'text-slate-700 leading-relaxed' : 'flex items-start gap-3 text-slate-700'}
              >
                {!isOrdered && <Check className="text-emerald-500 h-5 w-5 mt-1 flex-shrink-0" />}
                <span dangerouslySetInnerHTML={createMarkup(html)} />
              </li>
            );
          })}
        </ListComponent>
      );
    }

    case 'alertBox':
      return (
        <div key={index} className="my-6 p-4 border-l-4 border-amber-500 bg-amber-50 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-500" aria-hidden="true" />
            </div>
            <div className="ml-3">
              <p
                className="text-sm text-amber-800"
                dangerouslySetInnerHTML={createMarkup(resolveContent(item))}
              />
            </div>
          </div>
        </div>
      );

    case 'checkbox': {
      const items = Array.isArray(item.items) ? item.items : [];
      return (
        <div key={index} className="my-6 p-4 border-2 border-slate-300 bg-slate-50 rounded-lg">
          <div className="flex items-start gap-3 mb-4">
            <CheckSquare className="h-6 w-6 text-emerald-600 shrink-0" />
            <p
              className="font-semibold text-slate-800"
              dangerouslySetInnerHTML={createMarkup(item.label ?? resolveContent(item))}
            />
          </div>
          <ul className="space-y-3 list-inside ml-4">
            {items.map((listItem, liIndex) => {
              const html = resolveListItem(listItem);
              return (
                <li key={liIndex} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                  <CheckCircle className="text-emerald-500 w-5 h-5 mt-1 shrink-0" />
                  <span dangerouslySetInnerHTML={createMarkup(html)} />
                </li>
              );
            })}
          </ul>
        </div>
      );
    }

    case 'recitalBox': {
      const items = Array.isArray(item.items) ? item.items : [];
      return (
        <div key={index} className="my-6 p-4 border-l-4 border-slate-300 bg-slate-50 rounded-r-lg">
          <div className="flex items-center gap-2 mb-3">
            <Quote className="h-5 w-5 text-slate-500" aria-hidden="true" />
            <h3 className="text-md font-semibold text-slate-600 uppercase tracking-wider">{item.title}</h3>
          </div>
          <div className="pl-7 space-y-2">
            {items.map((recitalItem, liIndex) => {
              const html = resolveListItem(recitalItem);
              return (
                <p
                  key={liIndex}
                  className="text-sm italic text-slate-600"
                  dangerouslySetInnerHTML={createMarkup(html)}
                />
              );
            })}
          </div>
        </div>
      );
    }

    default:
      return null;
  }
};
